/**
 * Shaping for the map's Alerts panel.
 *
 * Kept apart from the component so the arithmetic is testable without a DOM,
 * and dependency-light so `node --test` can import it directly. ESM needs the
 * explicit `.js` on relative imports for that to work.
 */

/** Severity colours, per the design: High red, Medium orange, Low blue. */
export const SEVERITY_COLORS = {
  high: '#DC2626',
  medium: '#F97316',
  low: '#3B82F6',
};

export const SEVERITY_LABELS = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/**
 * Normalise `market_impact` to a severity key.
 *
 * The field is free text and arrives localised — 'High' and 'Alto' are the
 * same thing — so the Spanish spellings map alongside the English rather than
 * falling through to a default that would paint a high-impact alert blue.
 */
export const severityOf = (alert) => {
  const raw = String(alert?.market_impact || '').trim().toLowerCase();
  if (['high', 'alto', 'alta'].includes(raw)) return 'high';
  if (['medium', 'medio', 'media', 'moderate'].includes(raw)) return 'medium';
  if (['low', 'bajo', 'baja'].includes(raw)) return 'low';
  return 'low';
};

/** Severity order for sorting: worst first. */
const SEVERITY_RANK = { high: 0, medium: 1, low: 2 };

/**
 * Is this alert live?
 *
 * The two alert sources disagree on how they say so: the digest carries a
 * boolean-ish `is_active` that can arrive as the string 'False', and the
 * extreme feed carries a `status` string. Treating either loosely would
 * silently include expired alerts in a count the user reads as current.
 */
export const isActiveAlert = (alert) => {
  if (alert?.status != null && alert.status !== '') {
    return String(alert.status).trim().toLowerCase() === 'active';
  }
  const a = alert?.is_active;
  if (a === false || a === 0 || a === '0') return false;
  if (typeof a === 'string' && ['false', 'no'].includes(a.trim().toLowerCase())) return false;
  return true;
};

/** `Number(null)` is 0 and `new Date(undefined)` is Invalid; both are rejected first. */
const timeOf = (v) => {
  if (v == null || v === '') return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

/** First usable date across the several names the two feeds use. */
export const alertStart = (alert) => {
  const candidates = [alert?.start_date, alert?.alert_date, alert?.from_date,
    alert?.fromdate, alert?.date, alert?.created_at, alert?.last_updated];
  for (const c of candidates) {
    const t = timeOf(c);
    if (t != null) return t;
  }
  return null;
};

/**
 * Human timing, e.g. "in 2 days" / "3 days ago" / "Today".
 * Returns null when the alert carries no usable date, so the caller can omit
 * the element rather than render "Invalid Date".
 */
export const relativeTime = (ms, now = Date.now()) => {
  if (ms == null) return null;
  const dayMs = 86400000;
  const startOf = (t) => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime(); };
  const days = Math.round((startOf(ms) - startOf(now)) / dayMs);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 1) return `in ${days} days`;
  return `${Math.abs(days)} days ago`;
};

/** "HEAVY RAIN" from "heavy_rain" / "Heavy Rain". */
export const eventTypeLabel = (alert) => {
  const raw = alert?.specific_alert_type || alert?.alert_type || alert?.event_type || '';
  const s = String(raw).replace(/[_-]+/g, ' ').trim();
  return s ? s.toUpperCase() : null;
};

/** Commodity chips, de-duplicated, preserving order. */
export const commodityTags = (alert) => {
  const list = Array.isArray(alert?.commodities_affected) && alert.commodities_affected.length
    ? alert.commodities_affected
    : [alert?.commodity];
  const seen = new Set();
  const out = [];
  for (const c of list) {
    if (c == null || c === '') continue;
    const s = String(c).trim();
    const k = s.toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out;
};

/** "Uruapan, Michoacán" — region then country, whichever exist. */
export const placeLabel = (row) =>
  [row?.region, row?.country].filter((p) => p != null && String(p).trim() !== '')
    .map((p) => String(p).trim()).join(', ');

/**
 * Active weather alerts, worst first, then soonest.
 *
 * Both feeds are merged because the panel presents one "Weather" list; they
 * are separate upstream only because they come from different pipelines.
 */
export const buildWeatherAlerts = (extremeAlerts = [], digestAlerts = []) => {
  const all = [...(extremeAlerts || []), ...(digestAlerts || [])].filter(isActiveAlert);
  return all
    .map((a) => ({ ...a, _severity: severityOf(a), _start: alertStart(a) }))
    .sort((x, y) => {
      const s = SEVERITY_RANK[x._severity] - SEVERITY_RANK[y._severity];
      if (s !== 0) return s;
      if (x._start == null) return 1;
      if (y._start == null) return -1;
      return x._start - y._start;
    });
};

export default {
  SEVERITY_COLORS,
  SEVERITY_LABELS,
  severityOf,
  isActiveAlert,
  alertStart,
  relativeTime,
  eventTypeLabel,
  commodityTags,
  placeLabel,
  buildWeatherAlerts,
};
